import { ChangeDetectorRef, Component, signal, WritableSignal, input, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { merge } from 'lodash-es';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../../services/api-services/user.service';
import { NotificationDialComponent } from '../../utils/dialogues/notification-dial/notification-dial.component';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { PasswordStrengthDirective } from '../../utils/directives/password-strength/pwd-strength.directive';

@Component({
  selector: 'app-change-pwd-form',
  templateUrl: './change-pwd-form.component.html',
  styleUrls: ['./change-pwd-form.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatIcon,
    PasswordStrengthDirective,
    MatError,
    MatButton,
  ],
})
export class ChangePwdFormComponent {
  /** Inject services. */
  private readonly userService = inject(UserService);
  private readonly dialogRef = inject(MatDialog);
  private readonly cdRef = inject(ChangeDetectorRef);

  readonly userId = input.required<number>();

  usrPwdForm = new FormGroup(
    {
      usrPwdNew: new FormControl('', [Validators.required, Validators.minLength(8)]),
      usrPwdConfirm: new FormControl('', [Validators.required]),
      usrPwdOld: new FormControl('', [Validators.required]),
    },
    { validators: this.verifyPasswords }
  );

  // Compare both password fields
  verifyPasswords(control: AbstractControl) {
    const newPwd = control.get('usrPwdNew');
    const confirmPwd = control.get('usrPwdConfirm');
    if (newPwd !== null && confirmPwd !== null) {
      if (newPwd.value !== confirmPwd.value) {
        return { noPwdMatch: true };
      }
    }
    return null;
  }

  // toggle for visibility of passwords
  hideOldPwd = signal(true);
  hideNewPwd = signal(true);
  hideConfPwd = signal(true);
  pwdNewErrorMsg = signal('');
  noPwdMatch = signal(false);

  constructor() {
    merge(this.usrPwdForm.statusChanges, this.usrPwdForm.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updateErrorMessage();
        this.cdRef.markForCheck();
      });
  }

  get usrPwdNew() {
    return this.usrPwdForm.get('usrPwdNew');
  }

  async updatePassword() {
    const newPwd = this.usrPwdNew?.value;
    const oldPwd = this.usrPwdForm.get('usrPwdOld')?.value;
    if (newPwd && oldPwd) {
      const res = await this.userService.updateUserPassword(this.userId(), oldPwd, newPwd);
      if (res.success) {
        this.showNotificationDial('Password changed', 'Your new password has been saved.');
        this.usrPwdForm.reset();
      } else {
        this.showNotificationDial('Some error occurred', res.msg);
      }
    } else {
      this.showNotificationDial(
        'Some error occurred',
        'Password cannot be changed as password values are missing.'
      );
    }
  }

  showNotificationDial(title: string, msg: string) {
    this.dialogRef.open(NotificationDialComponent, {
      disableClose: false,
      data: {
        title: title,
        message: msg,
      },
    });
  }

  toggleHidePwd(event: MouseEvent, hide: WritableSignal<boolean>) {
    hide.set(!hide());
    event.stopPropagation();
    event.preventDefault();
  }

  updateErrorMessage() {
    if (this.usrPwdForm.hasError('noPwdMatch')) {
      this.noPwdMatch.set(true);
    } else {
      this.noPwdMatch.set(false);
    }

    // Check new user password
    if (this.usrPwdNew?.errors?.['required']) {
      this.pwdNewErrorMsg.set('You must enter a value');
    } else if (this.usrPwdNew?.errors?.['passwordStrength']) {
      this.pwdNewErrorMsg.set('Stronger password required.');
    } else if (this.usrPwdNew?.errors?.['minlength']) {
      this.pwdNewErrorMsg.set(
        `Password needs at least ${this.usrPwdNew.errors['minlength']['requiredLength']} characters.`
      );
    } else {
      this.pwdNewErrorMsg.set('');
    }
  }
}
